import { userService } from '../auth/userService.js';
import { getTenantConfig } from '../tenant/tenantService.js';
import { featureFlagsService } from '../tenant/featureFlagsService.js';

export const isUUID = (str) => {
  if (!str || typeof str !== 'string') return false;
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(str);
};

/**
 * Verifica se um conteúdo segmentado é elegível para os segmentos do usuário.
 * - eligibleIds vazio/nulo → público (aparece para todos)
 * - userSegmentIds vazio → conteúdo segmentado não aparece (bug 1 corrigido)
 * - OR entre todos os segmentos do usuário (bug 2 corrigido)
 */
export const hasSegmentMatch = (eligibleIds, userSegmentIds) => {
  if (!eligibleIds || eligibleIds.length === 0) return true;
  if (!userSegmentIds || userSegmentIds.length === 0) return false;
  return eligibleIds.some(id => userSegmentIds.includes(id));
};

/**
 * Resolve a lista efetiva de segmentIds para o usuário atual.
 * Se o usuário tem segmentos próprios, usa esses; senão usa o SEGMENT_DEFAULT do tenant.
 */
export const toEffectiveSegmentIds = (userSegmentIds, defaultSegmentId) => {
  if (userSegmentIds && userSegmentIds.length > 0) return userSegmentIds;
  return defaultSegmentId ? [defaultSegmentId] : [];
};

/**
 * Versão síncrona pronta para uso em componentes: lê cache + fallback default.
 * Substitui o boilerplate JSON.parse(localStorage…) + getDefaultSegmentIdSync() espalhado no projeto.
 */
export const getEffectiveSegmentIdsSync = () =>
  toEffectiveSegmentIds(
    userService.getCurrentSegmentIdsSync(),
    userService.getDefaultSegmentIdSync()
  );

/**
 * Versão assíncrona: busca segmentos frescos da API se necessário.
 * @param {string} language
 * @returns {Promise<{userSegmentIds: string[], defaultSegmentId: string|null}>}
 */
export const getSegmentContext = async (language = 'pt-BR') => {
  let userSegmentIds = [];
  let defaultSegmentId = null;

  if (userService.isAuthenticated()) {
    try {
      const segments = await userService.getUserSegments();
      userSegmentIds = segments.map(s => s.segmentId).filter(isUUID);
    } catch (error) {
      console.warn('Error fetching user segments, using default:', error);
    }
  }

  try {
    const tenantConfig = getTenantConfig();
    if (tenantConfig?.SEGMENT_DEFAULT && isUUID(tenantConfig.SEGMENT_DEFAULT)) {
      defaultSegmentId = tenantConfig.SEGMENT_DEFAULT;
    } else {
      const featureFlags = await featureFlagsService.getFeatureFlags(language);
      if (featureFlags?.SEGMENT_DEFAULT && isUUID(featureFlags.SEGMENT_DEFAULT)) {
        defaultSegmentId = featureFlags.SEGMENT_DEFAULT;
      }
    }
  } catch (error) {
    console.warn('Error fetching SEGMENT_DEFAULT:', error);
  }

  return { userSegmentIds, defaultSegmentId };
};

/**
 * Seleciona UM asset da lista pelo segmento do usuário.
 * Prioridade: segmentos do usuário (mais recente primeiro) → SEGMENT_DEFAULT → público → primeiro disponível.
 * @param {Array} assets
 * @param {string[]} userSegmentIds - Segmentos do usuário, já ordenados mais-recente-primeiro
 * @param {string|null} defaultSegmentId
 * @returns {Object|null}
 */
export const filterAssetBySegment = (assets, userSegmentIds = [], defaultSegmentId = null) => {
  if (!assets || assets.length === 0) return null;

  // Prioridade 1: primeiro segmento do usuário que bate com algum asset
  if (userSegmentIds.length > 0) {
    for (const segmentId of userSegmentIds) {
      if (!isUUID(segmentId)) continue;
      const found = assets.find(
        a => a.profiles && Array.isArray(a.profiles) && a.profiles.includes(segmentId)
      );
      if (found) return found;
    }
  }

  // Prioridade 2: SEGMENT_DEFAULT
  if (defaultSegmentId && isUUID(defaultSegmentId)) {
    const found = assets.find(
      a => a.profiles && Array.isArray(a.profiles) && a.profiles.includes(defaultSegmentId)
    );
    if (found) return found;
  }

  // Prioridade 3: asset público (sem profiles)
  const pub = assets.find(a => !a.profiles || (Array.isArray(a.profiles) && a.profiles.length === 0));
  if (pub) return pub;

  return assets[0] || null;
};

/**
 * Retorna TODOS os assets compatíveis com os segmentos do usuário.
 * Prioridade: segmentos do usuário → SEGMENT_DEFAULT → públicos.
 * @param {Array} assets
 * @param {string[]} userSegmentIds
 * @param {string|null} defaultSegmentId
 * @returns {Array}
 */
export const filterAssetsBySegment = (assets, userSegmentIds = [], defaultSegmentId = null) => {
  if (!assets || assets.length === 0) return [];

  const result = [];
  const seen = new Set();

  // Prioridade 1: assets que batem com qualquer segmento do usuário
  if (userSegmentIds.length > 0) {
    for (const asset of assets) {
      if (
        asset.profiles &&
        Array.isArray(asset.profiles) &&
        asset.profiles.some(p => userSegmentIds.includes(p)) &&
        !seen.has(asset.id)
      ) {
        result.push(asset);
        seen.add(asset.id);
      }
    }
  }

  // Prioridade 2: assets com SEGMENT_DEFAULT
  if (defaultSegmentId && isUUID(defaultSegmentId)) {
    for (const asset of assets) {
      if (
        asset.profiles &&
        Array.isArray(asset.profiles) &&
        asset.profiles.includes(defaultSegmentId) &&
        !seen.has(asset.id)
      ) {
        result.push(asset);
        seen.add(asset.id);
      }
    }
  }

  // Prioridade 3: assets públicos (sem profiles)
  for (const asset of assets) {
    if (
      (!asset.profiles || (Array.isArray(asset.profiles) && asset.profiles.length === 0)) &&
      !seen.has(asset.id)
    ) {
      result.push(asset);
      seen.add(asset.id);
    }
  }

  return result.length > 0 ? result : assets;
};

// Alias mantido para compatibilidade com callers que importavam getSegmentIds (footerService, etc.)
export const getSegmentIds = getSegmentContext;
